import { zodResolver } from "@hookform/resolvers/zod";
import { LucideLoaderCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FiDownload } from "react-icons/fi";
import { z } from "zod";

import { Clipboard } from "@/components/Clipboard";
import { LazyImage } from "@/components/LazyImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

async function toDataURL(url: string) {
  const blob = await fetch(url).then((res) => res.blob());
  return URL.createObjectURL(blob);
}
const Shorten = ({ domains }: { domains: string[]; dark?: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [generatedLink, setGeneratedLink] = useState<any | undefined>(
    undefined,
  );

  const clearGeneratedLink = () => setGeneratedLink(undefined);

  const formSchema = z.object({
    longUrl: z.string().url({
      message: "Enter a valid URL",
    }),
    domain: z.string(),
    backHalf: z.string().min(0).max(8),
    statusCode: z.enum(["301", "302", "307", "308"]).default("301"),
    deepLink: z.boolean().default(true),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      longUrl: "",
      domain: domains[0],
      backHalf: "",
      statusCode: "301",
      deepLink: true,
    },
  });
  // 2. Define a submit handler.
  function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.

    fetch("https://" + import.meta.env.PUBLIC_API_URL + "/tools/shortener", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        link: values.longUrl,
        id: values.backHalf ? "/" + values.backHalf : undefined,
        domain: values.domain,
        statusCode: values.statusCode,
        deepLink: values.deepLink,
      }),
    })
      .then((res) => res.json())
      .then((link) => {
        setGeneratedLink(link);
        setLoading(false);
        if (link.url) {
          // @ts-expect-error 2lazy2fix
          posthog.capture("tools_shorturl_created", link);
          form.reset();
        } else {
          if (link.message === "Too Many Requests") {
            form.setError("longUrl", {
              message: "Too many requests, please try again in a few minutes",
            });
          } else if (link.message === "Forbidden") {
            form.setError("longUrl", {
              message: "This URL is not allowed",
            });
          } else
            form.setError("backHalf", {
              message: "This Back-Half is already taken",
            });
        }
      });

    setLoading(true);
  }
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <motion.div
            layout
            transition={{ duration: 0.3 }}
            className="dark:text-foreground w-full"
          >
            <h2 className="text-4xl font-semibold">Shorten a Long Link</h2>
            <div className="mt-10">
              <FormField
                control={form.control}
                name="longUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="md:text-lg">
                      Paste a long URL
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Paste your Loooong URL here"
                        className="bg-background dark:bg-foreground"
                        {...field}
                      />
                    </FormControl>
                    {/* <FormDescription>
                  This is your public display name.
                </FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-3 flex w-full gap-2 md:w-[60%]">
                <FormField
                  control={form.control}
                  name={`domain`}
                  defaultValue={domains[0]}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:text-lg">Domain</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger
                            className="bg-background dark:bg-foreground text-slate-900"
                            aria-label="Choose a domain"
                          >
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {domains.map((opt) => (
                            <SelectItem value={opt} key={`domain-${opt}`}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <span className={"mt-9 text-3xl font-semibold"}>/</span>

                <FormField
                  control={form.control}
                  name="backHalf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:text-lg">
                        Custom Back-Half
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="my-cool-link"
                          className="bg-background dark:bg-foreground"
                          {...field}
                        />
                      </FormControl>
                      {/* <FormDescription>
                  This is your public display name.
                </FormDescription> */}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {loading ? (
                <Button
                  className="bg-primary/75 mt-5 cursor-not-allowed"
                  type="submit"
                >
                  <LucideLoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                  Get your link
                </Button>
              ) : (
                <Button className="mt-5" type="submit">
                  Get your link
                </Button>
              )}
              <Button
                variant={"ghost"}
                className="hover:text-primary ml-auto"
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                Advanced Options
              </Button>
            </div>
            <AnimatePresence initial={false}>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{
                  height: isExpanded ? 250 : 0,
                  opacity: isExpanded ? 1 : 0,
                }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  overflow: "hidden",
                  padding: "0 1px",
                }}
                className="flex flex-col gap-4"
              >
                <Separator className="mt-4" />
                <FormField
                  control={form.control}
                  name="statusCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="md:text-lg">Status Code</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={"301"}
                        >
                          <FormControl>
                            <SelectTrigger
                              className="bg-background dark:bg-foreground py-1 text-slate-900"
                              aria-label="Choose a status code"
                            >
                              <SelectValue placeholder="Select a type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[
                              "301 Moved Permanently",
                              "302 Found",
                              "307 Temporary Redirect",
                              "308 Permanent Redirect",
                            ].map((opt) => (
                              <SelectItem
                                value={opt.slice(0, 3)}
                                key={`statusCode-${opt}`}
                              >
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      {/* <FormDescription>
                  This is your public display name.
                </FormDescription> */}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deepLink"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Deep-Linking</FormLabel>
                        <FormDescription>
                          Deep-linking is a technique that causes the link to
                          open in the app instead of the browser on mobile
                          devices.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </form>
      </Form>
      <Dialog
        open={!!generatedLink && generatedLink.url}
        onOpenChange={clearGeneratedLink}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Congratulations! Your link is ready to share.
            </DialogTitle>
            <DialogDescription>
              <div className="mt-12">
                <p className="text-muted-foreground text-lg">
                  Here's your unique link:
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <Input
                    className="h-10"
                    value={generatedLink?.url}
                    onChange={() => null}
                    placeholder="Your link"
                  />
                  <Clipboard
                    content={generatedLink?.url}
                    className="text-primary size-7"
                  />
                </div>
                <p className="text-muted-foreground mt-10 text-lg">
                  Here's your QR code:
                </p>
                <div className="mt-3 flex gap-4">
                  <div className="rounded-lg border-2 p-4">
                    <LazyImage
                      src={
                        "https://img.smartgraphics.mk/qr?w=150&h=150&value=" +
                        encodeURIComponent(generatedLink?.url)
                      }
                      height={150}
                      width={150}
                    />
                  </div>

                  <Button
                    variant={"ghost"}
                    onClick={async () => {
                      const a = document.createElement("a");
                      a.href = await toDataURL(
                        "https://img.smartgraphics.mk/qr?value=" +
                          encodeURIComponent(generatedLink?.url),
                      );
                      a.download = "qr.png";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                    size={"icon"}
                    className="size-7"
                  >
                    <FiDownload className="text-primary h-full w-full" />
                  </Button>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => clearGeneratedLink()}>
            Create a new link
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { Shorten };
